import { Logger } from '@feature-hub/core';
import { UeElement, UeReference } from '@oneaudi/falcon-tools';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import { Headline, TextLink, Text } from '@oneaudi/unified-web-components';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import React from 'react';
import styled from 'styled-components';
import {
  SYS_BREAKPOINT_XS,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_2_XL,
} from '@oneaudi/unified-web-common';
import { ChunkedEditorialTeaserComponent } from '../../types';
import { parseAssetValue } from '../../utils/parseAssetValue';
import { TrackingClickEvent } from '../tracking';
import { convertRichTextWithFootnotes } from '../utils';
import { ImageThirdFirst, ImageThird, ImageHalf, ImageFull } from './Images';
import { LegalInfo } from './LegalInfo';

const StyledComponent = styled.div<{
  index: number;
  gridArea: string;
  numberOfComponents: number;
}>`
  ${({ numberOfComponents, gridArea }) => {
    if (numberOfComponents === 1) {
      return `
        flex-direction: column;
        grid-area: ${gridArea};
      `;
    }
    return '';
  }}

  .component__inner {
    display: flex;
    height: auto;
    flex-direction: column;
    height: 100%;
  }

  /* Important: Styled for UEReference component, remove if there is a another
  way to styled UEReference components with StyledComponents adding classNames, etc. */
  & > div:first-child {
    display: flex;
    height: auto;
    flex-direction: column;
    height: 100%;
  }

  @media screen and (min-width: ${SYS_BREAKPOINT_XS}}) and (max-width: ${+SYS_BREAKPOINT_MD.replace('px', '') - 1}px) {
    ${({ index }: { index: number }) =>
      index > 0
        ? `
      margin-block-start: var(${SYS_BREAKPOINT_XL});
`
        : ''}
  }

  @media screen and (min-width: ${SYS_BREAKPOINT_MD}) and (max-width: ${+SYS_BREAKPOINT_LG.replace('px', '') - 1}px) {
    ${({ numberOfComponents, gridArea }) => {
      if (numberOfComponents === 3) {
        return `
        flex-direction: column;
        grid-area: ${gridArea};
            `;
      }
      return '';
    }}
  }

`;

const Media = styled.div`
  position: relative;
  height: 0;
  overflow: hidden;
  padding-top: 56.25%;

  div {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
  }
`;

const TextArea = styled.div`
  height: 100%;

  @media screen and (min-width: ${SYS_BREAKPOINT_XS}) and (max-width: ${+SYS_BREAKPOINT_MD.replace(
      'px',
      '',
    ) - 1}px) {
    padding-block-start: var(--spacing-relative-lg);
  }

  @media screen and (min-width: ${SYS_BREAKPOINT_MD}) {
    padding-block-start: var(--spacing-relative-lg);
    padding-inline-end: var(--spacing-relative-xl);
  }

  @media screen and (min-width: ${SYS_BREAKPOINT_2_XL}) {
    padding-block-start: var(--spacing-relative-lg);
    padding-inline-end: var(--spacing-relative-xl);
  }
`;

const ButtonContainer = styled.div`
  &:first-child {
    margin-block-start: 0;
  }

  display: flex;
  justify-content: start;
  align-items: center;
  padding: ${() => `var(--spacing-relative-md) 0 0 0`};
`;

const LegalInfoContainer = styled.div``;

export const Teaser = ({
  content: {
    imageAsset,
    image,
    heading,
    headingTag,
    text,
    links: unfilteredLinks,
    consumptionAndEmissions,
    disclaimers,
    originalIndex,
  },
  index,
  numberOfComponents,
  sendClickEvent,
  localeService,
  oneGraphService,
  loggerService,
}: {
  content: ChunkedEditorialTeaserComponent;
  index: number;
  numberOfComponents: number;
  sendClickEvent?: (event: TrackingClickEvent, componentIndex: number) => void;
  localeService: LocaleServiceV1;
  oneGraphService: OneGraphServiceV1;
  loggerService?: Logger;
}): React.ReactElement => {
  const hasLegalInfo = !!consumptionAndEmissions?.length || !!disclaimers?.length;
  // eslint-disable-next-line no-nested-ternary
  const gridArea = index === 0 ? 'a' : index === 1 ? 'b' : 'c';

  // remove links without label
  const links =
    unfilteredLinks && unfilteredLinks.length ? unfilteredLinks.filter((link) => link.label) : [];

  const [isCSR, setIsCSR] = React.useState(false);

  React.useEffect(() => {
    setIsCSR(true);
  }, []);

  const handleTextLinkClick = (e: React.MouseEvent, targetURL: string, linkLabel: string) => {
    e.stopPropagation();
    if (sendClickEvent)
      sendClickEvent(
        {
          name: 'click on teaser',
          elementName: 'text link',
          value: heading,
          pos: `${originalIndex + 1}`,
          targetURL,
          label: linkLabel,
        },
        originalIndex,
      );
  };

  const renderImage = () => {
    const assetSrc = imageAsset?.src ? parseAssetValue(imageAsset.src.assetValue).src : image?.src;
    const imageSrc = assetSrc || '';

    switch (true) {
      case numberOfComponents === 3 && index === 0:
        return <ImageThirdFirst src={imageSrc} alt={image?.alt} />;
      case numberOfComponents === 3:
        return <ImageThird src={imageSrc} alt={image?.alt} />;
      case numberOfComponents === 2:
        return <ImageHalf src={imageSrc} alt={image?.alt} />;
      default:
        return <ImageFull src={imageSrc} alt={image?.alt} />;
    }
  };

  return (
    <StyledComponent
      gridArea={gridArea}
      numberOfComponents={numberOfComponents}
      index={originalIndex}
    >
      <UeReference propertyPath={`components.${originalIndex}`} as="div">
        <div style={{ height: '100%' }} className="component__inner">
          <Media>
            <div>{renderImage()}</div>
          </Media>
          <TextArea>
            {heading && (
              <UeElement type="text" property="heading" label="Headline">
                {(ueProps) => (
                  <Headline
                    // variant="order3" as={headingTag || 'h3'}
                    variant={{ order: '3', type: 'headline', weight: 'normal' }}
                    tag={headingTag || 'h3'}
                    {...ueProps}
                  >
                    {isCSR
                      ? convertRichTextWithFootnotes(heading)
                      : renderTextWithFootnotesReferencesV2(heading)}
                  </Headline>
                )}
              </UeElement>
            )}

            {text && (
              <UeElement type="richtext" property="text" label="Copy">
                {(ueProps) => (
                  <Text
                    variant={{ style: 'normal', order: '1' }}
                    margin={['var(--spacing-relative-xs)', 0, 0, 0]}
                    {...ueProps}
                  >
                    {isCSR
                      ? convertRichTextWithFootnotes(text)
                      : renderTextWithFootnotesReferencesV2(text)}
                  </Text>
                )}
              </UeElement>
            )}

            {Boolean(links && links.length) && (
              <div>
                <UeReference propertyPath={`components.${index}`} label="CTAs">
                  {links &&
                    links.map((link, i) => {
                      return (
                        <ButtonContainer key={link.label ?? link.url}>
                          <TextLink
                            size="md"
                            variant="standalone"
                            key={link.label}
                            aria-label={link.ariaLabel || ''}
                            target={link.openInNewTab ? '_blank' : undefined}
                            icon="forward"
                            iconPosition="trailing"
                            href={link.url}
                            onClick={(e) => handleTextLinkClick(e, link.url, link.label)}
                          >
                            <UeElement
                              type="text"
                              property={`links_${i}_label`}
                              label={`link ${i} `}
                            >
                              {link.label}
                            </UeElement>
                          </TextLink>
                        </ButtonContainer>
                      );
                    })}
                </UeReference>
              </div>
            )}

            {hasLegalInfo && (
              <LegalInfoContainer>
                <LegalInfo
                  consumptionAndEmissions={consumptionAndEmissions || []}
                  disclaimers={disclaimers}
                  localeService={localeService}
                  oneGraphService={oneGraphService}
                  loggerService={loggerService}
                />
              </LegalInfoContainer>
            )}
          </TextArea>
        </div>
      </UeReference>
    </StyledComponent>
  );
};
